import type { CSSProperties } from "react";
import { readableTextColor, sanitizeTenantColor } from "../../lib/colorUtils";
import { IconLock } from "../icons/icons";
import styles from "./TenantPill.module.css";

interface TenantPillProps {
  label: string;
  color: string;
  logoUrl?: string;
  active?: boolean;
  /** True once an administrator has suspended this business; shows a lock badge
   * so the owner/administrator switcher flags it before it's even selected. */
  suspended?: boolean;
  onClick?: () => void;
}

/** A compact business switcher: the tenant's own logo or a color-coded
 * initial, plus its name, in a segmented-pill control. Static (no onClick)
 * anywhere it is used only to identify a business, clickable to switch
 * between businesses in the owner/administrator workspace. */
export function TenantPill({ label, color, logoUrl, active = true, suspended = false, onClick }: TenantPillProps) {
  const safeColor = sanitizeTenantColor(color);
  const style = {
    "--avatar-color": safeColor,
    "--avatar-ink": readableTextColor(safeColor, "#201c17", "#ffffff"),
  } as CSSProperties;

  const avatar = logoUrl ? (
    <img className={styles.avatarImage} src={logoUrl} alt="" style={style} />
  ) : (
    <span className={styles.avatarInitial} style={style} aria-hidden="true">
      {label.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );

  const content = (
    <>
      {avatar}
      <span className={styles.pillLabel}>
        {label}
        {suspended && <span className="visually-hidden"> — suspended</span>}
      </span>
      {suspended && <IconLock className={styles.suspendedIcon} width={13} height={13} />}
    </>
  );

  if (!onClick) {
    return (
      <span className={`${styles.pill} ${styles.staticPill} ${active ? styles.active : ""}`}>{content}</span>
    );
  }

  return (
    <button
      type="button"
      className={`${styles.pill} ${active ? styles.active : ""}`}
      onClick={onClick}
      aria-current={active ? "true" : undefined}
    >
      {content}
    </button>
  );
}
